import { motion } from "framer-motion";
import { ArrowRight, MessageCircle, Mail, Phone } from "lucide-react";
import { CONTACT_EMAIL, CONTACT_PHONE_HREF, whatsappHref } from "@/lib/contact";

const mailLink = (subject, body) =>
  `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;

const STEPS = [
  {
    num: "01",
    title: "Buy",
    slug: "buy",
    tag: "We help you buy",
    copy: "Primary and resale opportunities matched to your budget and investment objective.",
    img: "https://www.fairfoxitinfra.com/public/media/images/certificate/eon-noida-03-atrium-1710830821.png",
    alt: "EON atrium render",
    wa: whatsappHref("Hi Ashish, I am interested in buying at EON Fairfox and would like to request a site visit."),
    mail: mailLink("Buying Enquiry — EON Fairfox", "Hi Homewiz.in team,\n\nI am interested in buying at EON Fairfox and would like to request a site visit.\n\nMy details:\n"),
  },
  {
    num: "02",
    title: "Fit Out",
    slug: "fit-out",
    tag: "We do the interiors",
    copy: "Professional commercial interiors designed to make the space ready for occupation.",
    img: "https://www.fairfoxitinfra.com/public/media/images/vertical/office-1-1718104631.jpg",
    alt: "EON office space render",
    wa: whatsappHref("Hi Ashish, I am interested in fit-out advisory for my EON Fairfox unit. My unit details are as follows: "),
    mail: mailLink("Fit-Out Enquiry — EON Fairfox", "Hi Homewiz.in team,\n\nI am interested in fit-out advisory for my EON Fairfox unit. My unit details are as follows:\n"),
  },
  {
    num: "03",
    title: "Lease",
    slug: "lease",
    tag: "We help you lease",
    copy: "We help position your completed space for leasing and connect you with potential occupiers.",
    img: "https://www.fairfoxitinfra.com/public/media/images/certificate/eon-noida-11-food-court-hr-1710831733.png",
    alt: "EON food court render",
    wa: whatsappHref("Hi Ashish, I am interested in leasing assistance for my EON Fairfox unit. My unit details are as follows: "),
    mail: mailLink("Leasing Enquiry — EON Fairfox", "Hi Homewiz.in team,\n\nI am interested in leasing assistance for my EON Fairfox unit. My unit details are as follows:\n"),
  },
];

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 44 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-100px" },
  transition: { duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] },
});

const iconBtn =
  "w-10 h-10 rounded-full border border-[#D4AF37]/40 text-[#D4AF37] flex items-center justify-center hover:bg-[#D4AF37] hover:text-[#0B0C0E] hover:shadow-[0_0_20px_rgba(212,175,55,0.35)] transition-all duration-300";

export default function Journey() {
  return (
    <section id="journey" data-testid="journey-section" className="py-28 sm:py-36">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.p {...fadeUp()} className="font-mono text-xs uppercase tracking-[0.35em] text-[#D4AF37]">
          The Investor Journey
        </motion.p>
        <motion.h2
          {...fadeUp(0.1)}
          className="mt-4 font-display text-3xl sm:text-4xl lg:text-6xl font-semibold tracking-tight text-[#F3F4F6] max-w-3xl"
        >
          Buy. Fit Out. <span className="gold-text italic">Lease.</span>
        </motion.h2>
        <motion.p {...fadeUp(0.18)} className="mt-5 max-w-xl text-base sm:text-lg text-[#9CA3AF] leading-relaxed">
          A complete solution from acquisition to leasing.
        </motion.p>

        <div className="mt-16 grid md:grid-cols-3 gap-5">
          {STEPS.map((s, i) => (
            <motion.article
              key={s.num}
              data-testid={`journey-step-${s.num}`}
              {...fadeUp(0.12 * i)}
              className="group relative bg-[#121418] border border-white/[0.07] rounded-2xl overflow-hidden hover:border-[#D4AF37]/40 transition-colors duration-500"
            >
              <div className="relative h-52 overflow-hidden">
                <img
                  src={s.img}
                  alt={s.alt}
                  loading="lazy"
                  className="w-full h-full object-cover grayscale-[35%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#121418] to-transparent" />
                <span className="absolute top-4 left-5 font-display text-5xl font-semibold text-white/15 group-hover:text-[#D4AF37]/50 transition-colors duration-500">
                  {s.num}
                </span>
              </div>
              <div className="p-7">
                <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">{s.tag}</div>
                <h3 className="mt-2 font-display text-2xl font-semibold text-[#F3F4F6] flex items-center gap-3">
                  {s.title}
                  {i < 2 && (
                    <ArrowRight size={18} className="text-[#D4AF37]/60 group-hover:translate-x-1.5 transition-transform duration-500" />
                  )}
                </h3>
                <p className="mt-3 text-sm text-[#9CA3AF] leading-relaxed">{s.copy}</p>
                <div className="mt-5 flex items-center gap-3" data-testid={`journey-${s.slug}-contact-row`}>
                  <a href={s.wa} target="_blank" rel="noopener noreferrer" data-testid={`journey-${s.slug}-whatsapp`} aria-label={`${s.title} on WhatsApp`} className={iconBtn}>
                    <MessageCircle size={15} />
                  </a>
                  <a href={s.mail} data-testid={`journey-${s.slug}-email`} aria-label={`${s.title} by email`} className={iconBtn}>
                    <Mail size={15} />
                  </a>
                  <a href={CONTACT_PHONE_HREF} data-testid={`journey-${s.slug}-call`} aria-label={`${s.title} by phone`} className={iconBtn}>
                    <Phone size={15} />
                  </a>
                  <span className="ml-1 font-mono text-[10px] uppercase tracking-[0.18em] text-[#6B7280]">
                    Chat · Email · Call
                  </span>
                </div>
              </div>
            </motion.article>
          ))}
        </div>

        <motion.div
          data-testid="journey-gold-strip"
          {...fadeUp(0.1)}
          className="mt-14 rounded-2xl bg-gradient-to-r from-[#AA7C11] via-[#D4AF37] to-[#F3E5AB] px-8 py-9 sm:px-12 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4"
        >
          <span className="font-display text-2xl sm:text-4xl font-semibold tracking-tight text-[#0B0C0E]">
            One Property. One Team.
          </span>
          <span className="flex items-center gap-3 font-mono text-xs sm:text-sm uppercase tracking-[0.25em] text-[#0B0C0E]/80">
            Acquisition <ArrowRight size={14} /> Interiors <ArrowRight size={14} /> Leasing
          </span>
        </motion.div>
      </div>
    </section>
  );
}
